import { OpportunityFlowItem, OpportunityHistoryUser } from 'types/opportunity';

export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value,
  );

export const formatCompactCurrency = (value: number): string => {
  if (Math.abs(value) >= 1000000) {
    return `R$ ${(value / 1000000).toFixed(2).replace('.', ',')} mi`;
  }

  if (Math.abs(value) >= 1000) {
    return `R$ ${Math.round(value / 1000).toLocaleString('pt-BR')} mil`;
  }

  return `R$ ${Math.round(value).toLocaleString('pt-BR')}`;
};

export const formatPercentage = (value: number, fractionDigits = 2): string =>
  `${value.toLocaleString('pt-BR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}%`;

export const formatDate = (value: string): string =>
  new Intl.DateTimeFormat('pt-BR').format(
    new Date(`${value.slice(0, 10)}T12:00:00`),
  );

export const formatDateTime = (value: string): string =>
  new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));

export const getOwnerInitials = (name: string): string => {
  const sanitizedName = name
    .replace(/\([^)]*\)/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, '')
    .trim();

  return sanitizedName
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
};

export const getOwnerAvatarColor = (name: string): string => {
  const initials = getOwnerInitials(name);
  const hash = Array.from(initials).reduce(
    (total, character) => total * 31 + character.charCodeAt(0),
    0,
  );
  const hue = Math.abs(hash) % 360;

  return `hsl(${hue}, 62%, 46%)`;
};

type ClientNameFields = {
  name?: string | null;
  corporate_name?: string | null;
  fantasy_name?: string | null;
};

export const getOpportunityClientName = (
  client: ClientNameFields | null | undefined,
): string =>
  client?.fantasy_name || client?.corporate_name || client?.name || '';

export const formatOpportunityLocale = (
  locale: OpportunityFlowItem['locale'],
): string => {
  if (!locale?.city) return '';

  return locale.state?.abbreviation
    ? `${locale.city}/${locale.state.abbreviation}`
    : locale.city;
};

export const getOpportunityValue = (item: OpportunityFlowItem): number =>
  Number(item.total ?? item.proposal_value ?? item.estimated_value ?? 0);

export const getOpportunityHistoryUserName = (
  user: OpportunityHistoryUser | null,
): string => {
  if (!user) return 'Sistema';

  if (user.document_type === 'cnpj')
    return (
      [user.corporate_name, user.fantasy_name].filter(Boolean).join(' - ') ||
      user.name
    );

  return user.name;
};
